'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from "@/components/ui/AppIcon";
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import toast from 'react-hot-toast';

interface Ticket {
  id: string;
  title: string;
  job: string;
  priority: 'high' | 'medium' | 'low';
  time: string;
  type: string;
  assigned_pm_id?: string;
  created_at: string;
  pm_status: 'open' | 'acknowledged' | 'in_progress' | 'resolved';
  ai_priority_score: number | null;
  ai_score_reason: string | null;
  sla_hours: number;
}

const priorityConfig = {
  high: { label: "High", bg: "bg-error/10", text: "text-error", dot: "bg-error" },
  medium: { label: "Medium", bg: "bg-warning/10", text: "text-warning", dot: "bg-warning" },
  low: { label: "Low", bg: "bg-success/10", text: "text-success", dot: "bg-success" },
};

const pmStatusConfig = {
  open: { label: "Open", bg: "bg-muted", text: "text-muted-foreground", icon: "ClockIcon" },
  acknowledged: { label: "Acknowledged", bg: "bg-blue-500/10", text: "text-blue-500", icon: "EyeIcon" },
  in_progress: { label: "In Progress", bg: "bg-warning/10", text: "text-warning", icon: "ArrowPathIcon" },
  resolved: { label: "Resolved", bg: "bg-success/10", text: "text-success", icon: "CheckCircleIcon" },
};

const SLA_DEFAULTS: Record<string, number> = {
  high: 4,
  medium: 24,
  low: 72,
};

const DEMO_TICKETS: Ticket[] = [];

function mapRow(row: any): Ticket {
  return {
    id: row.id,
    title: row.title,
    job: row.job,
    priority: row.priority as Ticket['priority'],
    time: row.ticket_time,
    type: row.ticket_type,
    assigned_pm_id: row.assigned_pm_id,
    created_at: row.created_at,
    pm_status: (row.pm_status as Ticket['pm_status']) || 'open',
    ai_priority_score: row.ai_priority_score ?? null,
    ai_score_reason: row.ai_score_reason ?? null,
    sla_hours: row.sla_hours ?? SLA_DEFAULTS[row.priority] ?? 24,
  };
}

function getElapsedInfo(createdAt: string, slaHours: number) {
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let elapsed = '';
  if (diffDays > 0) elapsed = `${diffDays}d ${diffHours % 24}h`;
  else if (diffHours > 0) elapsed = `${diffHours}h ${diffMins % 60}m`;
  else elapsed = `${diffMins}m`;

  const slaBreach = diffHours >= slaHours;
  const slaWarning = !slaBreach && diffHours >= slaHours * 0.75;

  return { elapsed, slaBreach, slaWarning };
}

function getAIScoreColor(score: number | null): string {
  if (score === null) return 'text-muted-foreground';
  if (score >= 8) return 'text-error';
  if (score >= 5) return 'text-warning';
  return 'text-success';
}

function getAIScoreBg(score: number | null): string {
  if (score === null) return 'bg-muted';
  if (score >= 8) return 'bg-error/10';
  if (score >= 5) return 'bg-warning/10';
  return 'bg-success/10';
}

export default function TicketsWidget({ onEscalationClick }: { onEscalationClick?: () => void }) {
  const { currentUser, isSuperAdmin } = useAuth();
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [tickets, setTickets] = useState<Ticket[]>(DEMO_TICKETS);
  const [loading, setLoading] = useState(true);
  const [scoringId, setScoringId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());
  const [showAllModal, setShowAllModal] = useState(false);

  // Tick every minute to update elapsed timers
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const supabase = createClient();

    async function fetchTickets() {
      try {
        // Try pm-john-001 first (demo PM)
        const { data: pmData } = await supabase
          .from('tickets')
          .select('*')
          .eq('assigned_pm_id', 'pm-john-001')
          .order('created_at', { ascending: false });

        if (pmData && pmData.length > 0) {
          setTickets(pmData.map(mapRow));
          return;
        }

        // Fallback: try by currentUser.id
        const { data, error } = await supabase
          .from('tickets')
          .select('*')
          .eq('assigned_pm_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (!error && data && data.length > 0) {
          setTickets(data.map(mapRow));
        } else {
          setTickets(DEMO_TICKETS);
        }
      } catch {
        setTickets(DEMO_TICKETS);
      } finally {
        setLoading(false);
      }
    }

    fetchTickets();

    const channel = supabase
      .channel('tickets_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tickets' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const newTicket = mapRow(payload.new);
          if (isSuperAdmin || newTicket.assigned_pm_id === currentUser.id) {
            setTickets((prev) => [newTicket, ...prev.filter(t => !t.id.startsWith('TKT-00'))]);
          }
        } else if (payload.eventType === 'UPDATE') {
          setTickets((prev) => prev.map((t) => t.id === payload.new.id ? mapRow(payload.new) : t));
        } else if (payload.eventType === 'DELETE') {
          setTickets((prev) => prev.filter((t) => t.id !== (payload.old as any).id));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUser.id, isSuperAdmin]);

  const handlePMStatusChange = useCallback(async (ticketId: string, newStatus: Ticket['pm_status']) => {
    setUpdatingId(ticketId);
    const supabase = createClient();
    const { error } = await supabase
      .from('tickets')
      .update({ pm_status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    if (error) {
      toast.error('Failed to update ticket status');
    } else {
      setTickets((prev) => prev.map((t) => t.id === ticketId ? { ...t, pm_status: newStatus } : t));
      toast.success(`Ticket marked as ${pmStatusConfig[newStatus].label}`);
    }
    setUpdatingId(null);
  }, []);

  const handleAutoScore = useCallback(async (ticket: Ticket) => {
    setScoringId(ticket.id);
    try {
      const result = await getChatCompletion(
        'GEMINI',
        'gemini/gemini-2.5-flash',
        [
          {
            role: 'system',
            content: `You are a roofing project management assistant. Score ticket urgency from 1-10 (10 = most urgent). 
Return ONLY valid JSON: {"score": <number 1-10>, "reason": "<one sentence explanation under 20 words>"}.
Consider: customer impact, financial risk, crew/job blockage, safety, time sensitivity.`,
          },
          {
            role: 'user',
            content: `Ticket: "${ticket.title}"\nType: ${ticket.type}\nJob: ${ticket.job}\nPriority: ${ticket.priority}\nStatus: ${ticket.pm_status}\nOpen for: ${getElapsedInfo(ticket.created_at, ticket.sla_hours).elapsed}`,
          },
        ],
        { max_completion_tokens: 100 }
      );

      const content = result?.choices?.[0]?.message?.content || '';
      let parsed: { score: number; reason: string } | null = null;
      try {
        parsed = JSON.parse(content);
      } catch {
        const match = content.match(/\{[\s\S]*\}/);
        if (match) parsed = JSON.parse(match[0]);
      }

      if (parsed && typeof parsed.score === 'number') {
        const supabase = createClient();
        const safeReason = typeof parsed.reason === 'string' ? parsed.reason : String(parsed.reason || '');
        await supabase
          .from('tickets')
          .update({ ai_priority_score: parsed.score, ai_score_reason: safeReason })
          .eq('id', ticket.id);

        setTickets((prev) =>
          prev.map((t) =>
            t.id === ticket.id
              ? { ...t, ai_priority_score: parsed!.score, ai_score_reason: safeReason }
              : t
          )
        );
        toast.success(`AI scored ticket ${ticket.id}: ${parsed.score}/10`);
      } else {
        toast.error('Could not parse AI score');
      }
    } catch {
      toast.error('AI scoring failed');
    }
    setScoringId(null);
  }, []);

  const handleScoreAll = useCallback(async () => {
    const unscored = tickets.filter((t) => t.ai_priority_score === null && (filter === 'all' || t.priority === filter));
    if (unscored.length === 0) {
      toast('All visible tickets already scored', { icon: '✓' });
      return;
    }
    for (const ticket of unscored) {
      await handleAutoScore(ticket);
    }
  }, [tickets, filter, handleAutoScore]);

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.priority === filter);
  const previewTickets = filtered.slice(0, 3);
  const hasMoreTickets = filtered.length > 3;
  const highCount = tickets.filter(t => t.priority === 'high').length;
  const slaBreachCount = tickets.filter(t => {
    const { slaBreach } = getElapsedInfo(t.created_at, t.sla_hours);
    return slaBreach && t.pm_status !== 'resolved';
  }).length;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon name="ExclamationCircleIcon" size={18} className="text-error" variant="solid" />
          <h3 className="font-heading text-base font-semibold text-foreground">Tickets & Escalations</h3>
          {!loading && highCount > 0 && (
            <span className="bg-error text-error-foreground text-xs font-caption px-1.5 py-0.5 rounded-full leading-tight">
              {highCount}
            </span>
          )}
          {!loading && slaBreachCount > 0 && (
            <span className="flex items-center gap-1 bg-error/10 text-error text-[10px] font-caption px-1.5 py-0.5 rounded-full">
              <Icon name="ExclamationTriangleIcon" size={10} variant="solid" />
              {slaBreachCount} SLA breach{slaBreachCount > 1 ? 'es' : ''}
            </span>
          )}
          {!loading && (
            <span className="flex items-center gap-1 text-[10px] font-caption text-success">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Live
            </span>
          )}
          {!isSuperAdmin && (
            <span className="flex items-center gap-1 text-[10px] font-caption text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              <Icon name="UserIcon" size={10} />
              Mine
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleScoreAll}
            title="Auto-score all unscored tickets with AI"
            className="flex items-center gap-1 px-3 rounded-md font-caption text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-standard"
            style={{ minHeight: '44px' }}
          >
            <Icon name="SparklesIcon" size={12} />
            Score All
          </button>
          <div className="flex gap-1 ml-1">
            {(['all', 'high', 'medium', 'low'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 rounded-md font-caption text-xs capitalize transition-standard ${
                  filter === f ? 'bg-accent text-white' : 'text-muted-foreground hover:bg-muted'
                }`}
                style={{ minHeight: '44px', minWidth: '44px' }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Ticket List — preview only (max 3) */}
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-6 h-6 border-2 border-error border-t-transparent rounded-full animate-spin mb-2" />
            <p className="font-body text-xs text-muted-foreground">Loading tickets...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Icon name="CheckCircleIcon" size={32} className="text-success mb-2" />
            <p className="font-body text-sm text-muted-foreground">No tickets found</p>
          </div>
        ) : (
          previewTickets.map((ticket) => {
            const p = priorityConfig[ticket.priority];
            const ps = pmStatusConfig[ticket.pm_status];
            const { elapsed, slaBreach, slaWarning } = getElapsedInfo(ticket.created_at, ticket.sla_hours);
            const isExpanded = expandedId === ticket.id;
            const isScoring = scoringId === ticket.id;
            const isUpdating = updatingId === ticket.id;

            return (
              <div
                key={ticket.id}
                className={`px-5 py-3 transition-standard cursor-pointer group ${
                  slaBreach && ticket.pm_status !== 'resolved' ?'bg-error/5 hover:bg-error/10 border-l-2 border-error' :'hover:bg-muted/50'
                }`}
                onClick={() => {
                  setExpandedId(isExpanded ? null : ticket.id);
                }}
              >
                {/* Row 1: ID, priority, type, SLA timer */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-data text-xs text-muted-foreground">{ticket.id}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-caption font-medium ${p.bg} ${p.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                      {p.label}
                    </span>
                    <span className="font-caption text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{ticket.type}</span>
                    {/* PM Status badge */}
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-caption font-medium ${ps.bg} ${ps.text}`}>
                      <Icon name={ps.icon as any} size={9} />
                      {ps.label}
                    </span>
                  </div>
                  {/* Escalation timer */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {slaBreach && ticket.pm_status !== 'resolved' ? (
                      <span className="flex items-center gap-1 text-[10px] font-caption font-semibold text-error bg-error/10 px-1.5 py-0.5 rounded-full">
                        <Icon name="ExclamationTriangleIcon" size={9} variant="solid" />
                        SLA {elapsed}
                      </span>
                    ) : slaWarning && ticket.pm_status !== 'resolved' ? (
                      <span className="flex items-center gap-1 text-[10px] font-caption font-semibold text-warning bg-warning/10 px-1.5 py-0.5 rounded-full">
                        <Icon name="ClockIcon" size={9} />
                        {elapsed}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-caption text-muted-foreground">
                        <Icon name="ClockIcon" size={9} />
                        {elapsed}
                      </span>
                    )}
                    {/* AI Score */}
                    {ticket.ai_priority_score !== null && (
                      <span className={`text-[10px] font-caption font-bold px-1.5 py-0.5 rounded-full ${getAIScoreBg(ticket.ai_priority_score)} ${getAIScoreColor(ticket.ai_priority_score)}`}>
                        AI {ticket.ai_priority_score}/10
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 2: Title + job */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-foreground truncate group-hover:text-primary transition-standard">{ticket.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="font-caption text-xs text-muted-foreground">{ticket.job}</p>
                      <button
                        onClick={e => { e.stopPropagation(); onEscalationClick?.(); }}
                        className="font-caption text-[10px] text-error/70 hover:text-error underline underline-offset-2 transition-standard"
                        title="Open Escalation Management"
                      >
                        Manage escalation
                      </button>
                    </div>
                  </div>
                  <Icon
                    name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"}
                    size={14}
                    className="text-muted-foreground group-hover:text-primary transition-standard flex-shrink-0 mt-1"
                  />
                </div>

                {/* Expanded panel: PM tracker + AI scoring */}
                {isExpanded && (
                  <div
                    className="mt-3 pt-3 border-t border-border space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* PM Response Tracker */}
                    <div>
                      <p className="font-caption text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">PM Response Status</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {(['open', 'acknowledged', 'in_progress', 'resolved'] as const).map((status) => {
                          const cfg = pmStatusConfig[status];
                          const isActive = ticket.pm_status === status;
                          return (
                            <button
                              key={status}
                              disabled={isUpdating}
                              onClick={() => handlePMStatusChange(ticket.id, status)}
                              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-caption font-medium transition-standard border ${
                                isActive
                                  ? `${cfg.bg} ${cfg.text} border-current`
                                  : 'bg-transparent text-muted-foreground border-border hover:bg-muted'
                              } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {isUpdating && isActive ? (
                                <span className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Icon name={cfg.icon as any} size={10} />
                              )}
                              {cfg.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* AI Priority Score */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="font-caption text-[10px] text-muted-foreground uppercase tracking-wide">AI Priority Score</p>
                        <button
                          disabled={isScoring}
                          onClick={() => handleAutoScore(ticket)}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-caption bg-primary/10 text-primary hover:bg-primary/20 transition-standard disabled:opacity-50"
                        >
                          {isScoring ? (
                            <span className="w-2.5 h-2.5 border border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Icon name="SparklesIcon" size={10} />
                          )}
                          {isScoring ? 'Scoring…' : ticket.ai_priority_score !== null ? 'Re-score' : 'Score with AI'}
                        </button>
                      </div>
                      {ticket.ai_priority_score !== null ? (
                        <div className={`flex items-center gap-2 p-2 rounded-lg ${getAIScoreBg(ticket.ai_priority_score)}`}>
                          <span className={`text-xl font-heading font-bold ${getAIScoreColor(ticket.ai_priority_score)}`}>
                            {ticket.ai_priority_score}
                            <span className="text-xs font-normal text-muted-foreground">/10</span>
                          </span>
                          <div className="flex-1 min-w-0">
                            {/* Score bar */}
                            <div className="w-full bg-border rounded-full h-1.5 mb-1">
                              <div
                                className={`h-1.5 rounded-full transition-all ${
                                  ticket.ai_priority_score >= 8 ? 'bg-error' :
                                  ticket.ai_priority_score >= 5 ? 'bg-warning' : 'bg-success'
                                }`}
                                style={{ width: `${ticket.ai_priority_score * 10}%` }}
                              />
                            </div>
                            {ticket.ai_score_reason && (
                              <p className="font-caption text-[10px] text-muted-foreground leading-tight">{typeof ticket.ai_score_reason === 'string' ? ticket.ai_score_reason : String(ticket.ai_score_reason)}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="font-caption text-[10px] text-muted-foreground italic">
                          Click "Score with AI" to auto-rank urgency based on ticket content.
                        </p>
                      )}
                    </div>

                    {/* SLA info */}
                    <div className="flex items-center justify-between text-[10px] font-caption text-muted-foreground">
                      <span>SLA target: {ticket.sla_hours}h</span>
                      <span className={slaBreach && ticket.pm_status !== 'resolved' ? 'text-error font-semibold' : ''}>
                        Open {elapsed}{slaBreach && ticket.pm_status !== 'resolved' ? ' — SLA BREACHED' : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-2 border-t border-border flex-shrink-0">
        <button
          className="w-full font-caption text-xs text-primary hover:text-secondary transition-standard text-center flex items-center justify-center"
          style={{ minHeight: '44px' }}
          onClick={() => setShowAllModal(true)}
        >
          View all tickets →
        </button>
      </div>

      {/* View All Tickets Modal */}
      {showAllModal && (
        <>
          <div
            className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAllModal(false)}
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon name="ExclamationCircleIcon" size={18} className="text-error" variant="solid" />
                  <h3 className="font-heading text-base font-semibold text-foreground">All Tickets & Escalations</h3>
                  <span className="font-caption text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {filtered.length} ticket{filtered.length !== 1 ? 's' : ''}
                  </span>
                  {highCount > 0 && (
                    <span className="bg-error text-error-foreground text-xs font-caption px-1.5 py-0.5 rounded-full leading-tight">
                      {highCount} high
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowAllModal(false)}
                  className="flex items-center justify-center rounded-lg hover:bg-muted transition-standard"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                  aria-label="Close all tickets modal"
                >
                  <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
                </button>
              </div>
              {/* Filter bar inside modal */}
              <div className="px-5 py-2 border-b border-border flex-shrink-0 flex items-center gap-1.5">
                {(['all', 'high', 'medium', 'low'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 rounded-md font-caption text-xs capitalize transition-standard ${
                      filter === f ? 'bg-accent text-white' : 'text-muted-foreground hover:bg-muted'
                    }`}
                    style={{ minHeight: '44px', minWidth: '44px' }}
                  >
                    {f}
                  </button>
                ))}
              </div>
              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto divide-y divide-border">
                {filtered.map((ticket) => {
                  const p = priorityConfig[ticket.priority];
                  const ps = pmStatusConfig[ticket.pm_status];
                  const { elapsed, slaBreach, slaWarning } = getElapsedInfo(ticket.created_at, ticket.sla_hours);
                  return (
                    <div
                      key={ticket.id}
                      className={`px-5 py-3 transition-standard ${
                        slaBreach && ticket.pm_status !== 'resolved' ? 'bg-error/5 border-l-2 border-error' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-data text-xs text-muted-foreground">{ticket.id}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-caption font-medium ${p.bg} ${p.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                            {p.label}
                          </span>
                          <span className="font-caption text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{ticket.type}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-caption font-medium ${ps.bg} ${ps.text}`}>
                            <Icon name={ps.icon as any} size={9} />
                            {ps.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {slaBreach && ticket.pm_status !== 'resolved' ? (
                            <span className="flex items-center gap-1 text-[10px] font-caption font-semibold text-error bg-error/10 px-1.5 py-0.5 rounded-full">
                              <Icon name="ExclamationTriangleIcon" size={9} variant="solid" />
                              SLA {elapsed}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[10px] font-caption text-muted-foreground">
                              <Icon name="ClockIcon" size={9} />
                              {elapsed}
                            </span>
                          )}
                          {ticket.ai_priority_score !== null && (
                            <span className={`text-[10px] font-caption font-bold px-1.5 py-0.5 rounded-full ${getAIScoreBg(ticket.ai_priority_score)} ${getAIScoreColor(ticket.ai_priority_score)}`}>
                              AI {ticket.ai_priority_score}/10
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="font-body text-sm text-foreground truncate">{ticket.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="font-caption text-xs text-muted-foreground">{ticket.job}</p>
                        <button
                          onClick={() => { setShowAllModal(false); onEscalationClick?.(); }}
                          className="font-caption text-[10px] text-error/70 hover:text-error underline underline-offset-2 transition-standard"
                        >
                          Manage escalation
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-border flex-shrink-0">
                <button
                  onClick={() => { setShowAllModal(false); onEscalationClick?.(); }}
                  className="w-full flex items-center justify-center gap-1.5 bg-error text-white rounded-lg font-caption text-xs font-medium hover:bg-error/80 transition-standard"
                  style={{ minHeight: '44px' }}
                >
                  <Icon name="ExclamationTriangleIcon" size={13} />
                  Open Escalation Management
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}